/**
 * /app/trusts/$id/properties — Fixed-property holdings (route shell).
 *
 * Screen impl in `./properties-screen.tsx`, lazy-loaded via `lazyRouteComponent`.
 * A deep trust sub-tab (property table + add-property modal), not a first-paint
 * route, so the body leaves the entry bundle (200 kB gzip budget). Same
 * shell+screen pattern as the sibling `./documents.tsx` / `./audit.tsx` tabs.
 * The committed routeTree.gen.ts imports only `Route` from here, so no plugin
 * regen is needed.
 *
 * PFO-01 (2026-08-27): `?add=true` lands with the add-property form already
 * open. It is how the holding-type switcher hands you from one add flow to
 * another without making you exit and start again — the dead end Paul walked
 * into on 22 August. The identical validator sits on `./companies.tsx`; the
 * Portfolios shell takes `?add=listed` instead, because its add flow is a form
 * on the page rather than a screen of its own.
 */

import { createFileRoute, lazyRouteComponent } from '@tanstack/react-router';

/** Permissive by construction: anything that is not a recognised truthy value
 *  yields `{}`, so a hand-typed or malformed query can never throw the route. */
function validateAddSearch(search: Record<string, unknown>): { add?: true } {
  const raw = search['add'];
  if (raw === true || raw === 'true' || raw === '1') return { add: true };
  return {};
}

export const Route = createFileRoute('/app/trusts/$id/properties')({
  component: lazyRouteComponent(() => import('./properties-screen'), 'PropertiesScreen'),
  validateSearch: validateAddSearch,
});
